import React, { useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

const COURIERS = [
  { id: "pedidosya", label: "PedidosYa" },
  { id: "uber", label: "Uber" },
  { id: "vimenpaq", label: "Vimenpaq" },
  { id: "caribepack", label: "Caribe Pack" },
  { id: "mock", label: "Mock" },
];

const STATUS_CYCLE = ["pending", "label_created", "picked_up", "in_transit", "out_for_delivery", "delivered"];
const ORDER_STATES = ["pending", "paid", "preparing", "shipped", "delivered", "finished", "cancelled"];

interface Props {
  filteredOrders: any[];
  products?: any[];
  lang: string;
  t: any;
  $: (n: number) => string;
  mapStatus: (s: string) => string;
  adminTrackingInputs: Record<string, string>;
  setAdminTrackingInputs: (fn: any) => void;
  adminSetTracking: (id: string, v: string) => void;
  adminCreateShipment: (id: string, courier: string, tracking: string) => void;
  adminAdvanceStatus: (id: string, status: string) => void;
}

export function AdminOrdersTable({ filteredOrders, products = [], lang, t, $, mapStatus, adminTrackingInputs, setAdminTrackingInputs, adminSetTracking, adminCreateShipment, adminAdvanceStatus }: Props) {
  const [courierInputs, setCourierInputs] = useState<Record<string, string>>({});
  const [simulating, setSimulating] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const productMap = useMemo(() => {
    const m: Record<string, any> = {};
    products.forEach((p: any) => {
      if (p?.dbId) m[p.dbId] = p;
      if (p?.id) m[p.id] = p;
    });
    return m;
  }, [products]);

  const toggleExpand = (id: string) => setExpanded(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const fmtDop = (n: number) => `RD$${Math.round(Number(n) || 0).toLocaleString("en-US")}`;
  const prodName = (pid: string) => {
    const p = productMap[pid];
    if (!p) return pid.slice(0, 8);
    const nm = typeof p.nm === "object" ? (p.nm[lang] || p.nm.en) : p.nm;
    return nm || p.sku || pid.slice(0, 8);
  };

  const visibleOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return filteredOrders.filter(o => {
      if (statusFilter && o.status !== statusFilter) return false;
      if (!q) return true;
      return (
        o.shortId?.toLowerCase().includes(q) ||
        o.cust?.toLowerCase().includes(q) ||
        o.tracking?.toLowerCase().includes(q) ||
        o.id?.toLowerCase().includes(q)
      );
    });
  }, [filteredOrders, search, statusFilter]);

  const allChecked = visibleOrders.length > 0 && visibleOrders.every(o => selected.has(o.id));
  const toggleAll = () => {
    setSelected(prev => {
      const n = new Set(prev);
      if (allChecked) visibleOrders.forEach(o => n.delete(o.id));
      else visibleOrders.forEach(o => n.add(o.id));
      return n;
    });
  };
  const toggleOne = (id: string) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const bulkSetStatus = useCallback(async (next: string) => {
    if (!selected.size) return;
    if (!confirm(lang === "es" ? `¿Cambiar ${selected.size} órdenes a "${next}"?` : `Change ${selected.size} orders to "${next}"?`)) return;
    setBulkBusy(true);
    try {
      const ids = [...selected];
      const { error } = await supabase.from("orders").update({ status: next }).in("id", ids);
      if (error) { alert(error.message); return; }
      // Optimistic: trigger parent refresh via adminAdvanceStatus side-effects is heavy;
      // simplest: reload page-level state by toggling location hash query — but we don't have that.
      // Mutate local visible list status so user gets feedback; parent will resync on next mount.
      visibleOrders.forEach((o: any) => { if (ids.includes(o.id)) o.status = next; });
      setSelected(new Set());
    } finally { setBulkBusy(false); }
  }, [selected, lang, visibleOrders]);

  const bulkExportCSV = useCallback(() => {
    const rows = visibleOrders.filter(o => selected.has(o.id));
    if (!rows.length) return;
    const headers = ["ID", "Customer", "Total", "Status", "Date", "Tracking"];
    const data = rows.map(o => [o.shortId, `"${o.cust}"`, o.total, o.status, o.date, o.tracking || ""]);
    const csv = [headers.join(","), ...data.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `orders-selected-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  }, [visibleOrders, selected]);

  const simulateNext = useCallback(async (orderId: string, trackingNum: string) => {
    setSimulating((p) => ({ ...p, [orderId]: true }));
    try {
      const courier = courierInputs[orderId] || "mock";
      // Get current shipment status to know what comes next
      const { data: shipData } = await supabase
        .from("shipments")
        .select("status")
        .eq("order_id", orderId)
        .eq("courier", courier)
        .maybeSingle();

      const currentStatus = shipData?.status || "pending";
      const currentIdx = STATUS_CYCLE.indexOf(currentStatus);
      const nextStatus = STATUS_CYCLE[Math.min(currentIdx + 1, STATUS_CYCLE.length - 1)];

      const { error } = await supabase.functions.invoke("shipping-test", {
        body: {
          courier,
          tracking_number: trackingNum,
          status: nextStatus,
          description: nextStatus === "delivered" ? "Package delivered" : `Status updated to ${nextStatus}`,
          order_id: orderId,
        },
      });

      if (error) {
        alert(lang === "es" ? `Error simulando: ${error.message}` : `Simulation error: ${error.message}`);
      }
    } finally {
      setSimulating((p) => ({ ...p, [orderId]: false }));
    }
  }, [courierInputs, lang]);

  return (
    <>
      {/* Search + status filter */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <input
          placeholder={lang === "es" ? "Buscar ID, cliente, tracking…" : "Search ID, customer, tracking…"}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: "1 1 240px", minWidth: 200, fontSize: 12, padding: "6px 10px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)" }}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ fontSize: 11, padding: "6px 8px", border: "1px solid var(--bd2)", borderRadius: 6, background: "var(--bg2)", color: "var(--tx)" }}
        >
          <option value="">{lang === "es" ? "Todos los estados" : "All statuses"}</option>
          {ORDER_STATES.map(s => <option key={s} value={s}>{t[mapStatus(s)] || s}</option>)}
        </select>
        <span style={{ fontSize: 10, color: "var(--tx3)" }}>
          {visibleOrders.length} {lang === "es" ? "visibles" : "visible"}
        </span>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, padding: "8px 10px", background: "var(--bg2)", border: "1px solid var(--ac)", borderRadius: 8, flexWrap: "wrap" }}>
          <strong style={{ fontSize: 11 }}>{selected.size} {lang === "es" ? "seleccionadas" : "selected"}</strong>
          <select
            disabled={bulkBusy}
            defaultValue=""
            onChange={e => { if (e.target.value) { bulkSetStatus(e.target.value); e.target.value = ""; } }}
            style={{ fontSize: 11, padding: "4px 6px", border: "1px solid var(--bd2)", borderRadius: 4, background: "var(--bg)", color: "var(--tx)" }}
          >
            <option value="">{lang === "es" ? "Cambiar estado a…" : "Set status to…"}</option>
            {ORDER_STATES.map(s => <option key={s} value={s}>{t[mapStatus(s)] || s}</option>)}
          </select>
          <button className="btn bg bs" style={{ fontSize: 10 }} onClick={bulkExportCSV}>
            {lang === "es" ? "Exportar CSV" : "Export CSV"}
          </button>
          <button className="btn bo bs" style={{ fontSize: 10 }} onClick={() => setSelected(new Set())}>
            {lang === "es" ? "Limpiar" : "Clear"}
          </button>
        </div>
      )}

      <table className="tbl">
      <thead>
        <tr>
          <th style={{ width: 30 }}>
            <input type="checkbox" checked={allChecked} onChange={toggleAll} />
          </th>
          <th>{t.orderId}</th>
          <th>Customer</th>
          <th>{lang === "es" ? "Fecha" : "Date"}</th>
          <th>{t.total}</th>
          <th>{t.status}</th>
          <th>Courier</th>
          <th>Tracking</th>
          <th>{t.action}</th>
        </tr>
      </thead>
      <tbody>
        {visibleOrders.map(o => (
          <React.Fragment key={o.id}>
          <tr style={{ cursor: "default" }}>
            <td>
              <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggleOne(o.id)} />
            </td>
            <td style={{ fontFamily: "monospace", fontSize: 10.5 }}>
              <button
                onClick={() => toggleExpand(o.id)}
                title={lang === "es" ? "Ver productos" : "View items"}
                style={{ background: "none", border: "none", color: "var(--ac)", cursor: "pointer", padding: 0, marginRight: 4, fontSize: 11 }}
              >
                {expanded.has(o.id) ? "▼" : "▶"}
              </button>
              {o.shortId}
            </td>
            <td style={{ fontSize: 12 }}>{o.cust}</td>
            <td style={{ fontSize: 10, color: "var(--tx3)" }}>{o.date}</td>
            <td style={{ fontWeight: 600 }}>{$(o.total)}</td>
            <td><span className={`stb ${mapStatus(o.status)}`}>{t[mapStatus(o.status)] || mapStatus(o.status)}</span></td>
            <td>
              <select
                style={{ width: 100, fontSize: 10, padding: "3px 4px", border: "1px solid var(--bd2)", borderRadius: 4, background: "var(--bg2)", color: "var(--tx)" }}
                value={courierInputs[o.id] || "mock"}
                onChange={e => setCourierInputs(p => ({ ...p, [o.id]: e.target.value }))}
              >
                {COURIERS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </td>
            <td>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <input
                  style={{ width: 100, fontSize: 10, padding: "3px 6px", border: "1px solid var(--bd2)", borderRadius: 4, background: "var(--bg2)", color: "var(--tx)" }}
                  placeholder="TRK-..."
                  value={adminTrackingInputs[o.id] !== undefined ? adminTrackingInputs[o.id] : o.tracking}
                  onChange={e => setAdminTrackingInputs((p: any) => ({ ...p, [o.id]: e.target.value }))}
                />
                {adminTrackingInputs[o.id] !== undefined && adminTrackingInputs[o.id] !== o.tracking && (
                  <button className="btn bp bs" style={{ fontSize: 9, padding: "2px 6px", height: "auto" }}
                    onClick={() => {
                      adminCreateShipment(o.id, courierInputs[o.id] || "mock", adminTrackingInputs[o.id]);
                      setAdminTrackingInputs((p: any) => { const n = { ...p }; delete n[o.id]; return n; });
                    }}>
                    ✓
                  </button>
                )}
                {o.tracking && (
                  <button
                    className="btn bo bs"
                    style={{ fontSize: 9, padding: "2px 6px", height: "auto" }}
                    disabled={simulating[o.id]}
                    onClick={() => simulateNext(o.id, o.tracking)}
                    title={lang === "es" ? "Simular siguiente evento" : "Simulate next event"}
                  >
                    {simulating[o.id] ? "…" : "▶"}
                  </button>
                )}
              </div>
            </td>
            <td>
              {o.status !== "finished" && (
                <button className="btn bg bs" onClick={() => adminAdvanceStatus(o.id, o.status)}>
                  {t.advance} → {mapStatus(["pending", "paid", "preparing", "shipped", "delivered", "finished"][["pending", "paid", "preparing", "shipped", "delivered", "finished"].indexOf(o.status) + 1] || "")}
                </button>
              )}
            </td>
          </tr>
          {expanded.has(o.id) && (
            <tr>
              <td colSpan={9} style={{ background: "var(--bg2)", padding: "10px 14px" }}>
                <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 6, color: "var(--tx2)" }}>
                  {lang === "es" ? "Productos pedidos" : "Ordered items"} ({(o.items || []).length})
                </div>
                {(!o.items || o.items.length === 0) ? (
                  <div style={{ fontSize: 11, color: "var(--tx3)" }}>
                    {lang === "es" ? "Sin items registrados" : "No items recorded"}
                  </div>
                ) : (
                  <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ color: "var(--tx3)", textAlign: "left" }}>
                        <th style={{ padding: "4px 6px" }}>{lang === "es" ? "Producto" : "Product"}</th>
                        <th style={{ padding: "4px 6px", width: 80 }}>SKU</th>
                        <th style={{ padding: "4px 6px", width: 50, textAlign: "center" }}>{lang === "es" ? "Cant." : "Qty"}</th>
                        <th style={{ padding: "4px 6px", width: 100, textAlign: "right" }}>{lang === "es" ? "Precio" : "Price"}</th>
                        <th style={{ padding: "4px 6px", width: 100, textAlign: "right" }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {o.items.map((it: any, idx: number) => {
                        const pid = it.product_id || it.pid;
                        const q = it.quantity ?? it.q ?? 0;
                        const price = Number(it.price_per_item ?? 0);
                        const p = productMap[pid];
                        return (
                          <tr key={idx} style={{ borderTop: "1px solid var(--bd2)" }}>
                            <td style={{ padding: "4px 6px" }}>{prodName(pid)}</td>
                            <td style={{ padding: "4px 6px", fontFamily: "monospace", color: "var(--tx3)" }}>{p?.sku || "—"}</td>
                            <td style={{ padding: "4px 6px", textAlign: "center" }}>{q}</td>
                            <td style={{ padding: "4px 6px", textAlign: "right" }}>{fmtDop(price)}</td>
                            <td style={{ padding: "4px 6px", textAlign: "right", fontWeight: 600 }}>{fmtDop(price * q)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </td>
            </tr>
          )}
          </React.Fragment>
        ))}
        {visibleOrders.length === 0 && (
          <tr><td colSpan={9} style={{ textAlign: "center", padding: 30, color: "var(--tx3)" }}>
            {lang === "es" ? "No hay órdenes en este rango de fechas" : "No orders in this date range"}
          </td></tr>
        )}
      </tbody>
    </table>
    </>
  );
}